import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from '../styles/admin/loginStyles';

import { endpoints } from '../api/endpoints';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [status, setStatus] = useState(''); // '', 'loading', 'success', 'error'
    const [message, setMessage] = useState('');
    const navigate = useNavigate();
    const token = searchParams.get('token');
    const hasCheckedToken = useRef(false);

    useEffect(() => {
        if (!token && !hasCheckedToken.current) {
            hasCheckedToken.current = true;
            setStatus('error');
            setMessage('Invalid password reset link. No token provided.');
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            setStatus('error');
            setMessage('Passwords do not match!');
            return;
        }

        if (newPassword.length < 8) {
            setStatus('error');
            setMessage('Password must be at least 8 characters long.');
            return;
        }

        setStatus('loading');
        setMessage('');

        try {
            const response = await fetch(endpoints.ResetPassword, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ token, newPassword }),
            });

            const data = await response.json();

            if (response.ok) {
                setStatus('success');
                setMessage(data.message || 'Password reset successfully!');

                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setStatus('error');
                setMessage(data.message || 'Failed to reset password. The link may be expired or invalid.');
            }
        } catch (error) {
            setStatus('error');
            setMessage('An error occurred. Please try again later.');
        }
    };

    return (
        <div style={styles.wrapper}>
            <div style={styles.form}>
                <h2 style={styles.heading}>Reset Password</h2>

                {status === 'success' && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <div style={{ fontSize: '48px', color: '#28a745', marginBottom: '20px' }}>
                            ✓
                        </div>
                        <p style={{ color: '#28a745', fontSize: '18px', marginBottom: '10px' }}>
                            {message}
                        </p>
                        <p style={{ color: '#666' }}>
                            Redirecting to login page...
                        </p>
                    </div>
                )}

                {status === 'error' && !token && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <div style={{ fontSize: '48px', color: '#dc3545', marginBottom: '20px' }}>
                            ✗
                        </div>
                        <p style={{ ...styles.errorMessage, fontSize: '16px' }}>
                            {message}
                        </p>
                        <button
                            style={{ ...styles.submitBtn, marginTop: '20px' }}
                            onClick={() => navigate('/forgot-password')}
                        >
                            Request New Reset Link
                        </button>
                    </div>
                )}

                {status !== 'success' && token && (
                    <>
                        <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px' }}>
                            Enter your new password below.
                        </p>

                        {status === 'error' && (
                            <div style={{ ...styles.errorMessage, marginBottom: '20px' }}>
                                {message}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <div style={styles.inputGroup}>
                                <label style={styles.label}>New Password</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    style={styles.input}
                                    placeholder="Enter new password"
                                    required
                                    disabled={status === 'loading'}
                                    minLength={8}
                                />
                                <small style={{ color: '#666', fontSize: '12px' }}>
                                    Minimum 8 characters
                                </small>
                            </div>

                            <div style={styles.inputGroup}>
                                <label style={styles.label}>Confirm Password</label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    style={styles.input}
                                    placeholder="Confirm new password"
                                    required
                                    disabled={status === 'loading'}
                                    minLength={8}
                                />
                            </div>

                            <button
                                type="submit"
                                style={{
                                    ...styles.submitBtn,
                                    opacity: status === 'loading' ? 0.6 : 1,
                                    cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                                }}
                                disabled={status === 'loading'}
                            >
                                {status === 'loading' ? 'Resetting...' : 'Reset Password'}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default ResetPassword;
